/**
 * 정책 변경 이력 (감사 로그)
 *
 *  단가·정책·패키지의 모든 변경을 추적. 누가·언제·왜 바꿨는지 영구 보관.
 *  분쟁 대응 및 내부 통제 목적.
 */
import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { creditApi, CreditPolicyChange } from '@/services/api/credit';

const TARGET_TYPE_LABEL: Record<string, { label: string; color: string }> = {
  PRICE:   { label: '단가',     color: 'bg-blue-50 text-blue-700' },
  POLICY:  { label: '정책',     color: 'bg-purple-50 text-purple-700' },
  PACKAGE: { label: '패키지',   color: 'bg-amber-50 text-amber-700' },
  PLAN:    { label: '정액제',   color: 'bg-emerald-50 text-emerald-700' },
};

const ACTION_LABEL: Record<string, { label: string; color: string }> = {
  CREATE:     { label: '추가',     color: 'bg-emerald-50 text-emerald-700' },
  UPDATE:     { label: '변경',     color: 'bg-blue-50 text-blue-700' },
  DEACTIVATE: { label: '비활성',   color: 'bg-gray-100 text-gray-600' },
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export function CreditChangesPage() {
  const [changes, setChanges] = useState<CreditPolicyChange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    creditApi.listChanges(100).then((list) => {
      setChanges(list);
      setLoading(false);
    });
  }, []);

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <History className="text-gray-500" /> 정책 변경 이력
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          모든 단가·정책·패키지 변경의 감사 로그입니다. 누가·언제·왜 바꿨는지 영구 보관.
        </p>
      </header>

      {/* 변경 이력 테이블 — PaymentList 와 통일된 스타일 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 w-[180px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  일시
                </th>
                <th className="px-6 py-3 w-[140px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  관리자
                </th>
                <th className="px-6 py-3 w-[280px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  대상
                </th>
                <th className="px-6 py-3 w-[110px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  액션
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  사유
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500">로딩 중…</td></tr>
              )}
              {!loading && changes.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500">변경 이력이 없어요</td></tr>
              )}
              {!loading && changes.map((c) => {
                const tt = TARGET_TYPE_LABEL[c.targetType];
                const aa = ACTION_LABEL[c.action];
                return (
                  <tr key={c.changeSeq} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                      {formatDate(c.dtRegist)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {c.adminName ?? `Admin#${c.adminSeq}`}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`shrink-0 px-2 py-1 text-xs font-semibold rounded-full ${tt?.color ?? 'bg-gray-100 text-gray-600'}`}>
                          {tt?.label ?? c.targetType}
                        </span>
                        <span className="text-gray-900 truncate" title={c.targetLabel}>{c.targetLabel}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${aa?.color ?? 'bg-gray-100 text-gray-600'}`}>
                        {aa?.label ?? c.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{c.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
